import { useEffect, useState } from 'react'
import { useDI } from '@/di/DIContext'
import centerLogo from '../assets/center-logo.svg'
import topDecoration from '../assets/top-logo.svg'

const QR_CODE_WIDTH = 122
const QR_CODE_MARGIN = 0
const QR_CODE_DARK_COLOR = '#000000'
const QR_CODE_LIGHT_COLOR = '#FFFFFF'

// 海报尺寸常量（参考 Figma 4906:1345）
const POSTER_WIDTH = 310
const POSTER_HEIGHT = 400
const POSTER_BORDER_RADIUS = 8
// 二维码容器
const QR_CONTAINER_WIDTH = 220
const QR_CONTAINER_HEIGHT = 253
const QR_CONTAINER_RADIUS = 8
// 二维码
const QR_SIZE_IN_POSTER = 180

// 提高画布分辨率的比例因子
const DRAW_SCALE = 4

interface UseQRCodeGeneratorResult {
  qrCodeDataUrl: string
  posterDataUrl: string
  qrCodeError: boolean
}

export function useQRCodeGenerator(
  promotionLink: string,
): UseQRCodeGeneratorResult {
  const { qrCodeService } = useDI()
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [posterDataUrl, setPosterDataUrl] = useState<string>('')
  const [qrCodeError, setQrCodeError] = useState<boolean>(false)

  useEffect(() => {
    if (!promotionLink) return

    setQrCodeError(false)

    const generateCodes = async () => {
      try {
        // 1. 生成展示用二维码（稍微加大一点点或者保持，主要供 UI 展示）
        const qrUrl = await qrCodeService.toDataURL(promotionLink, {
          width: QR_CODE_WIDTH * 2, // 提高 UI 显示质量
          margin: QR_CODE_MARGIN,
          color: {
            dark: QR_CODE_DARK_COLOR,
            light: QR_CODE_LIGHT_COLOR,
          },
        })
        setQrCodeDataUrl(qrUrl)

        // 2. 为海报生成高分辨率二维码
        // 确保海报里的二维码也是高清的：尺寸 = 目标宽度 * 缩放倍数
        const posterQrUrl = await qrCodeService.toDataURL(promotionLink, {
          width: QR_SIZE_IN_POSTER * DRAW_SCALE,
          margin: QR_CODE_MARGIN,
          color: {
            dark: QR_CODE_DARK_COLOR,
            light: QR_CODE_LIGHT_COLOR,
          },
        })

        // 3. 生成用于下载/分享的海报
        const canvas = document.createElement('canvas')
        canvas.width = POSTER_WIDTH * DRAW_SCALE
        canvas.height = POSTER_HEIGHT * DRAW_SCALE
        const ctx = canvas.getContext('2d')

        if (!ctx) throw new Error('Failed to get canvas context')

        // 使用 scale 让后续坐标不需要手动乘以 DRAW_SCALE
        ctx.scale(DRAW_SCALE, DRAW_SCALE)

        // 预加载资源
        const loadImg = (src: string) =>
          new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve(img)
            img.onerror = reject
            img.src = src
          })

        const [topDecoImg, centerLogoImg, qrImage] = await Promise.all([
          loadImg(topDecoration),
          loadImg(centerLogo),
          loadImg(posterQrUrl),
        ])

        // --- 绘制背景 ---
        // 渐变：顶部 #EBF6FF 到底部白色（约 36% 处）
        // linear-gradient(180deg, #EBF6FF 0%, #FFFFFF 36.08%)
        // 参考 Figma: to-[36.084%] to-white
        const gradient = ctx.createLinearGradient(0, 0, 0, POSTER_HEIGHT)
        gradient.addColorStop(0, '#EBF6FF')
        gradient.addColorStop(0.36, '#FFFFFF')
        gradient.addColorStop(1, '#FFFFFF')

        ctx.fillStyle = gradient
        ctx.beginPath()
        ctx.roundRect(0, 0, POSTER_WIDTH, POSTER_HEIGHT, POSTER_BORDER_RADIUS)
        ctx.fill()

        // --- 绘制顶部装饰 ---
        // 位置：左 110，上 35，宽 89，高 28
        ctx.drawImage(topDecoImg, 110, 35, 89, 28)

        // --- 绘制文案 1 ---
        // 文案: "Scan the QR code to start playing"
        // 字体: 14px 加粗，居中
        ctx.font = '600 14px sans-serif'
        ctx.fillStyle = '#000000'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'top'
        ctx.fillText('Scan the QR code to start playing', POSTER_WIDTH / 2, 71)

        // --- 绘制二维码容器 ---
        // 位置：左 45，上 111，宽 220，高 253
        // 背景: rgba(246, 246, 246, 0.37)，边框: #e7e7e7
        const containerX = 45
        const containerY = 111

        ctx.save()
        ctx.beginPath()
        ctx.roundRect(
          containerX,
          containerY,
          QR_CONTAINER_WIDTH,
          QR_CONTAINER_HEIGHT,
          QR_CONTAINER_RADIUS,
        )
        ctx.fillStyle = 'rgba(246, 246, 246, 0.37)'
        ctx.fill()
        ctx.lineWidth = 1
        ctx.strokeStyle = '#e7e7e7'
        ctx.stroke()
        ctx.restore()

        // --- 绘制二维码 ---
        // 位置：左 65，上 131，尺寸 180（Figma 绝对坐标）
        // 居中校验：容器中心 155，二维码中心 155
        ctx.drawImage(qrImage, 65, 131, QR_SIZE_IN_POSTER, QR_SIZE_IN_POSTER)

        // --- 绘制中心 Logo ---
        // 位置：左 135.61，上 201.65，尺寸 39.9
        // 取整：X=135，Y=201，尺寸=40
        ctx.drawImage(centerLogoImg, 135, 201, 40, 40)

        // --- 绘制文案 2（邀请链接） ---
        // 字体: 10px 常规，居中
        // 容器宽 220，最大文本宽度约 200
        ctx.font = '400 10px sans-serif'
        ctx.fillStyle = '#000000'
        ctx.textAlign = 'center'

        const MAX_TEXT_WIDTH = 200 // 容器宽 220 - 内边距
        const lineHeight = 14 // 10px 字体 + 行距
        const words = promotionLink.split('') // URL 按字符换行
        let line = ''
        const lines = []

        for (let n = 0; n < words.length; n++) {
          const testLine = line + words[n]
          const metrics = ctx.measureText(testLine)
          const testWidth = metrics.width
          if (testWidth > MAX_TEXT_WIDTH && n > 0) {
            lines.push(line)
            line = words[n]
          } else {
            line = testLine
          }
        }
        lines.push(line)

        // 绘制多行文本
        // 起始 Y：二维码下方 311 + 余量
        // 容器底部为 364
        // 余量高度：364 - 311 = 53
        // 文本高度：lines.length * lineHeight
        // 起始 Y = 311 + (53 - blockHeight) / 2

        const blockHeight = lines.length * lineHeight
        // 起始 Y = 311 + (53 - blockHeight) / 2

        ctx.textBaseline = 'middle'
        const initialY = 311 + (53 - blockHeight) / 2 + lineHeight / 2

        lines.forEach((l, i) => {
          ctx.fillText(l, POSTER_WIDTH / 2, initialY + i * lineHeight)
        })

        setPosterDataUrl(canvas.toDataURL('image/png'))
        setQrCodeError(false)
      } catch (err) {
        console.error('Failed to generate customized QR code:', err)
        setQrCodeError(true)
      }
    }

    generateCodes()
  }, [promotionLink, qrCodeService])

  return {
    qrCodeDataUrl,
    posterDataUrl,
    qrCodeError,
  }
}
